import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorMessage } from '@/components/ui/error-message';
import { authApi } from '@/api/auth';
import { toast } from '@/components/ui/use-toast';
import Routes from '@/router/routes';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';
import { emailSchema } from '@/lib/validationSchemas';
import { getErrorMessage, isNetworkError } from '@/utils/errorUtils';

const validationSchema = Yup.object().shape({
  email: emailSchema,
});

type FormData = Yup.InferType<typeof validationSchema>;

const defaultValues: FormData = {
  email: '',
};

export default function ForgotPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const email = watch('email');

  const onFormSubmit = async (data: FormData) => {
    setIsLoading(true);

    try {
      await authApi.forgotPassword(data.email);
      setIsSubmitted(true);
      toast({
        title: 'Email sent',
        description: 'Check your inbox for password reset instructions.',
      });
    } catch (err) {
      // TELV1-370 — branch on the actual error. forgot-password endpoints
      // typically return 200 even when the email doesn't exist (to avoid
      // email enumeration), so most real errors here are network failures
      // or rate-limits, NOT 404. The network branch tells the admin "your
      // request may have been sent" so they check their inbox before
      // retrying instead of spamming the endpoint.
      //
      // IMPORTANT: do NOT call `setIsSubmitted(true)` in any error branch
      // — the "Check your email" success screen at line 64 would render
      // and mislead the admin into waiting for an email that won't arrive.
      if (isNetworkError(err)) {
        toast({
          title: 'Network error',
          description:
            'Network error — your request may have been sent. Check your inbox in a few minutes before retrying.',
          variant: 'destructive',
        });
        return;
      }
      // 429 rate-limit, 500/502/503/504 server errors, or anything else —
      // surface the server message via getErrorMessage which already maps
      // each status to a friendly copy.
      const fallbackMessage = getErrorMessage(err, 'Failed to send reset email. Please try again.');
      toast({ title: 'Error', description: fallbackMessage, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
            <h2 className="mt-6 text-3xl font-bold text-foreground">Check your email</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              We&apos;ve sent password reset instructions to <strong>{email}</strong>
            </p>
          </div>

          <div className="rounded-md bg-green-50 dark:bg-green-900/30 p-4">
            <p className="text-sm text-green-700 dark:text-green-400">
              If you don&apos;t see the email, check your spam folder or try again with a different
              email address.
            </p>
          </div>

          <Link to={Routes.login.path}>
            <Button variant="outline" className="w-full">
              <ArrowLeft size={16} className="mr-2" />
              Back to login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
          <h2 className="mt-6 text-3xl font-bold text-foreground">Forgot your password?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter your email address and we&apos;ll send you instructions to reset your password.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onFormSubmit)}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
              Email address
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="admin@tellet.ai"
              {...register('email')}
              icon={<Mail size={20} />}
              hasError={!!errors.email}
            />
            <ErrorMessage message={errors.email?.message} />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send reset instructions'}
          </Button>

          <Link to={Routes.login.path}>
            <Button variant="ghost" className="w-full">
              <ArrowLeft size={16} className="mr-2" />
              Back to login
            </Button>
          </Link>
        </form>
      </div>
    </div>
  );
}
